/**
 * Polling Service - Handles periodic payment status checks
 * @module @one-payments/core/services
 */

import type { Adapters } from '../adapters/types.js';
import type { PaymentStatus } from '../types/state.js';
import type { ApiResponse, PaymentDataResult } from '../types/api.js';

/**
 * Polling configuration options
 */
export interface PollingConfig {
  /**
   * Interval between polls in milliseconds
   * @default 3000
   */
  interval?: number;

  /**
   * Maximum number of poll attempts
   * @default 60 (3 minutes at 3 second intervals)
   */
  maxAttempts?: number;

  /**
   * Timeout in milliseconds for the entire polling operation
   * @default 180000 (3 minutes)
   */
  timeout?: number;

  /**
   * Whether to ignore nextActionUrl field in responses
   * Used after 3DS completes to prevent showing modal again
   * @default false
   */
  ignore3DS?: boolean;

  /**
   * Whether to ignore qrCode field in responses
   * Used after QR code is scanned to prevent showing QR modal again
   * @default false
   */
  ignoreQR?: boolean;
}

/**
 * Payment status response from polling endpoint
 */
export interface PaymentStatusResponse {
  status: PaymentStatus;
  paymentIntentId: string;
  amount?: number;
  currency?: string;
  metadata?: Record<string, unknown>;
  nextActionUrl?: string;  // For 3DS authentication
  qrCode?: string;         // For QR/PayNow payments
  error?: {
    code: string;
    message: string;
  };
}

/**
 * Status update callback
 */
export type StatusCallback = (response: PaymentStatusResponse) => void;

/**
 * Poll start callback - fired when a poll request is about to be made
 * This is useful for detecting when getPaymentData is invoked (e.g., after 3DS form submission)
 */
export type PollStartCallback = () => void;

/**
 * Map API status to SDK status
 * The API returns "paid" but SDK expects "succeeded"
 */
function mapApiStatus(apiStatus: string): PaymentStatus {
  if (apiStatus === 'paid') {
    return 'succeeded';
  }
  return apiStatus as PaymentStatus;
}

/**
 * Payment Status Polling Service
 *
 * Polls the payment status endpoint at regular intervals until:
 * - Payment reaches a terminal state (succeeded/failed)
 * - Maximum attempts reached
 * - Timeout exceeded
 * - Service is stopped manually
 */
export class PollingService {
  private readonly adapters: Adapters;
  private readonly baseUrl: string;
  private readonly token: string;
  private readonly config: Required<PollingConfig>;
  private readonly onStatusUpdate: StatusCallback;
  private readonly onPollStart: PollStartCallback | undefined;

  private isPolling = false;
  private attempts = 0;
  private startTime = 0;
  private pollTimeoutId: ReturnType<typeof setTimeout> | null = null;

  constructor(
    adapters: Adapters,
    baseUrl: string,
    token: string,
    onStatusUpdate: StatusCallback,
    config: PollingConfig = {},
    onPollStart?: PollStartCallback
  ) {
    this.adapters = adapters;
    this.baseUrl = baseUrl;
    this.token = token;
    this.onStatusUpdate = onStatusUpdate;
    this.onPollStart = onPollStart;
    this.config = {
      interval: config.interval ?? 3000, // 3 seconds default
      maxAttempts: config.maxAttempts ?? 60, // 60 attempts = 3 minutes
      timeout: config.timeout ?? 180000, // 3 minutes
      ignore3DS: config.ignore3DS ?? false, // Default: don't ignore 3DS
      ignoreQR: config.ignoreQR ?? false, // Default: don't ignore QR
    };
  }

  /**
   * Start polling for payment status
   */
  public start(): void {
    if (this.isPolling) {
      return;
    }

    this.isPolling = true;
    this.attempts = 0;
    this.startTime = this.adapters.timer.now();
    this.scheduleNextPoll();
  }

  /**
   * Stop polling
   */
  public stop(): void {
    this.isPolling = false;
    if (this.pollTimeoutId !== null) {
      clearTimeout(this.pollTimeoutId);
      this.pollTimeoutId = null;
    }
  }

  /**
   * Check if currently polling
   */
  public get active(): boolean {
    return this.isPolling;
  }

  /**
   * Get number of attempts made
   */
  public get attemptCount(): number {
    return this.attempts;
  }

  /**
   * Schedule the next poll attempt
   */
  private scheduleNextPoll(): void {
    if (!this.isPolling) {
      return;
    }

    // Check if we've exceeded max attempts
    if (this.attempts >= this.config.maxAttempts) {
      this.stop();
      this.onStatusUpdate({
        status: 'failed',
        paymentIntentId: '',
        error: {
          code: 'POLLING_MAX_ATTEMPTS',
          message: `Maximum polling attempts (${this.config.maxAttempts}) exceeded`,
        },
      });
      return;
    }

    // Check if we've exceeded timeout
    const elapsed = this.adapters.timer.now() - this.startTime;
    if (elapsed >= this.config.timeout) {
      this.stop();
      this.onStatusUpdate({
        status: 'failed',
        paymentIntentId: '',
        error: {
          code: 'POLLING_TIMEOUT',
          message: `Polling timeout (${this.config.timeout}ms) exceeded`,
        },
      });
      return;
    }

    // Schedule next poll
    this.pollTimeoutId = setTimeout(() => {
      this.poll();
    }, this.config.interval);
  }

  /**
   * Perform a single poll request
   */
  private async poll(): Promise<void> {
    if (!this.isPolling) {
      return;
    }

    this.attempts++;

    // Notify that we're about to make a poll request
    // This is called BEFORE the HTTP request is made
    if (this.onPollStart) {
      this.onPollStart();
    }

    try {
      // Call the get-payment-data endpoint
      const response = await this.adapters.http.get<ApiResponse<PaymentDataResult>>(
        `${this.baseUrl}/payment-intent/get-payment-data`,
        {
          'x-token': this.token,
        }
      );

      const statusResponse: PaymentStatusResponse = {
        status: mapApiStatus(response.result.status),  // Map "paid" to "succeeded"
        paymentIntentId: response.result.paymentIntentId || '',
        ...(response.result.amount !== undefined && { amount: response.result.amount }),
        ...(response.result.currency !== undefined && { currency: response.result.currency }),
        ...(response.result.metadata !== undefined && { metadata: response.result.metadata }),
        ...(response.result.nextActionUrl !== undefined && { nextActionUrl: response.result.nextActionUrl }),
        ...(response.result.qrCode !== undefined && { qrCode: response.result.qrCode }),
      };

      // Check for 3DS requirement FIRST (highest priority)
      // BUT: Skip if ignore3DS config is true (after 3DS form submission, we only watch for final status)
      if (response.result.nextActionUrl && !this.config.ignore3DS) {
        this.stop();
        this.onStatusUpdate(statusResponse);
        return;
      }

      // Check for QR code SECOND
      // BUT: Skip if ignoreQR config is true (after QR is scanned, we only watch for final status)
      if (response.result.qrCode && !this.config.ignoreQR) {
        this.stop();
        this.onStatusUpdate(statusResponse);
        return;
      }

      // Check if status is TERMINAL (succeeded, failed, paid)
      // ONLY stop polling for terminal statuses - continue for ALL others
      const isTerminalStatus =
        response.result.status === 'succeeded' ||
        response.result.status === 'failed' ||
        response.result.status === 'paid';

      if (isTerminalStatus) {
        // Terminal status reached - stop polling and notify
        this.stop();
        this.onStatusUpdate(statusResponse);
        return;
      }

      // For ALL non-terminal statuses (processing, requires_action, in_progress, etc.)
      // Continue polling with interval requests until we reach terminal status
      this.scheduleNextPoll();
    } catch (error) {
      // On error, continue polling but notify callback
      const errorResponse: PaymentStatusResponse = {
        status: 'processing',
        paymentIntentId: '',
        error: {
          code: 'POLLING_ERROR',
          message: error instanceof Error ? error.message : 'Unknown polling error',
        },
      };

      this.onStatusUpdate(errorResponse);

      // Continue polling on error (might be transient)
      this.scheduleNextPoll();
    }
  }
}
